import React, { useState, useEffect } from "react";
import {
  Download,
  FileText,
  Database,
  Image as ImageIcon,
  CheckCircle2,
  AlertCircle,
  Settings,
  Package,
  X,
  FolderDown,
  Code,
  Table,
  Target,
  Calendar,
  User,
  Hash,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import { message } from "antd";
import axios from "../../utils/axios";

const ExportAnnotationsModal = ({
  isOpen,
  onClose,
  selectedProject,
  annotations,
  images,
}) => {
  const [selectedFormats, setSelectedFormats] = useState(["json"]);
  const [exportOptions, setExportOptions] = useState({
    includeImages: false,
    includeMetadata: true,
    normalizeCoordinates: false,
    splitDataset: false,
    trainSplit: 80,
    validationSplit: 15,
    testSplit: 5,
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  const exportFormats = [
    {
      id: "json",
      name: "JSON",
      description: "Standard JSON format with bounding boxes",
      icon: Code,
      extension: ".json",
      size: "Small",
    },
    {
      id: "csv",
      name: "CSV",
      description: "Comma-separated values for spreadsheet analysis",
      icon: Table,
      extension: ".csv",
      size: "Small",
    },
    {
      id: "coco",
      name: "COCO",
      description: "Common Objects in Context format for ML training",
      icon: Target,
      extension: ".json",
      size: "Medium",
    },
    {
      id: "yolo",
      name: "YOLO",
      description: "YOLO format with normalized coordinates",
      icon: Target,
      extension: ".txt",
      size: "Small",
    },
    // {
    //   id: "pascal",
    //   name: "Pascal VOC",
    //   description: "Pascal VOC XML format",
    //   icon: FileText,
    //   extension: ".xml",
    //   size: "Large",
    // },
  ];

  const handleFormatToggle = (formatId) => {
    setSelectedFormats((prev) =>
      prev.includes(formatId)
        ? prev.filter((id) => id !== formatId)
        : [...prev, formatId]
    );
  };

  const generateJSONExport = () => {
    const exportData = {
      project: {
        name: selectedProject.projectName,
        annotationGroup: selectedProject.annotationGroup,
        createdOn: selectedProject.createdOn,
        exportedOn: new Date().toISOString(),
        totalImages: images.length,
        totalAnnotations: annotations.reduce(
          (total, img) => total + (img.annotations?.length || 0),
          0
        ),
      },
      annotations: annotations.map((imgData) => ({
        imageUrl: imgData.imageUrl,
        imageName: imgData.imageUrl.split("/").pop(),
        annotations: imgData.annotations?.map((ann, idx) => ({
          id: idx,
          class: ann.class || "object",
          bbox: {
            x: exportOptions.normalizeCoordinates ? ann.x / 1000 : ann.x,
            y: exportOptions.normalizeCoordinates ? ann.y / 1000 : ann.y,
            width: exportOptions.normalizeCoordinates
              ? ann.width / 1000
              : ann.width,
            height: exportOptions.normalizeCoordinates
              ? ann.height / 1000
              : ann.height,
          },
          area: ann.width * ann.height,
          confidence: ann.confidence || 1.0,
        })) || [],
      })),
    };

    if (exportOptions.includeMetadata) {
      exportData.metadata = {
        format: "JSON",
        version: "1.0",
        coordinate_system: exportOptions.normalizeCoordinates
          ? "normalized"
          : "pixel",
        exported_by: JSON.parse(localStorage.getItem("user"))?.username || "unknown",
        export_timestamp: new Date().toISOString(),
      };
    }

    return JSON.stringify(exportData, null, 2);
  };

  const generateCSVExport = () => {
    const headers = [
      "image_name",
      "image_url",
      "annotation_id",
      "class",
      "x",
      "y",
      "width",
      "height",
      "area",
      "confidence",
    ];

    if (exportOptions.includeMetadata) {
      headers.unshift("project_name", "annotation_group", "exported_on");
    }

    const rows = [headers.join(",")];

    annotations.forEach((imgData) => {
      const imageName = imgData.imageUrl.split("/").pop();

      if (imgData.annotations?.length > 0) {
        imgData.annotations.forEach((ann, idx) => {
          const row = [
            imageName,
            imgData.imageUrl,
            idx,
            ann.class || "object",
            exportOptions.normalizeCoordinates
              ? (ann.x / 1000).toFixed(4)
              : ann.x,
            exportOptions.normalizeCoordinates
              ? (ann.y / 1000).toFixed(4)
              : ann.y,
            exportOptions.normalizeCoordinates
              ? (ann.width / 1000).toFixed(4)
              : ann.width,
            exportOptions.normalizeCoordinates
              ? (ann.height / 1000).toFixed(4)
              : ann.height,
            ann.width * ann.height,
            ann.confidence || 1.0,
          ];

          if (exportOptions.includeMetadata) {
            row.unshift(
              selectedProject.projectName,
              selectedProject.annotationGroup,
              new Date().toISOString()
            );
          }

          rows.push(row.join(","));
        });
      } else {
        const row = [
          imageName,
          imgData.imageUrl,
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
        ];

        if (exportOptions.includeMetadata) {
          row.unshift(
            selectedProject.projectName,
            selectedProject.annotationGroup,
            new Date().toISOString()
          );
        }

        rows.push(row.join(","));
      }
    });

    return rows.join("\n");
  };

  const generateCOCOExport = () => {
    const categories = [
      ...new Set(
        annotations.flatMap((img) =>
          img.annotations?.map((ann) => ann.class || "object") || []
        )
      ),
    ].map((name, idx) => ({
      id: idx + 1,
      name,
      supercategory: "object",
    }));

    const cocoData = {
      info: {
        description: `${selectedProject.projectName} - Exported Annotations`,
        version: "1.0",
        year: new Date().getFullYear(),
        contributor: selectedProject.annotationGroup,
        date_created: new Date().toISOString(),
      },
      licenses: [
        {
          id: 1,
          name: "Unknown License",
          url: "",
        },
      ],
      images: annotations.map((imgData, idx) => ({
        id: idx + 1,
        width: 1000, // Default width - should be actual image dimensions
        height: 1000, // Default height - should be actual image dimensions
        file_name: imgData.imageUrl.split("/").pop(),
        license: 1,
        flickr_url: "",
        coco_url: imgData.imageUrl,
        date_captured: new Date().toISOString(),
      })),
      annotations: [],
      categories,
    };

    let annotationId = 1;
    annotations.forEach((imgData, imgIdx) => {
      imgData.annotations?.forEach((ann) => {
        const categoryId =
          categories.find((cat) => cat.name === (ann.class || "object"))?.id || 1;

        cocoData.annotations.push({
          id: annotationId++,
          image_id: imgIdx + 1,
          category_id: categoryId,
          segmentation: [],
          area: ann.width * ann.height,
          bbox: [ann.x, ann.y, ann.width, ann.height],
          iscrowd: 0,
        });
      });
    });

    return JSON.stringify(cocoData, null, 2);
  };

  const generateYOLOExport = () => {
    const files = {};
    const classes = [
      ...new Set(
        annotations.flatMap((img) =>
          img.annotations?.map((ann) => ann.class || "object") || []
        )
      ),
    ];

    files["classes.txt"] = classes.join("\n");

    annotations.forEach((imgData) => {
      const imageName = imgData.imageUrl
        .split("/")
        .pop()
        .replace(/\.[^/.]+$/, "");
      const yoloAnnotations =
        imgData.annotations?.map((ann) => {
          const classId = classes.indexOf(ann.class || "object");
          const centerX = (ann.x + ann.width / 2) / 1000;
          const centerY = (ann.y + ann.height / 2) / 1000;
          const width = ann.width / 1000;
          const height = ann.height / 1000;

          return `${classId} ${centerX.toFixed(6)} ${centerY.toFixed(
            6
          )} ${width.toFixed(6)} ${height.toFixed(6)}`;
        }).join("\n") || "";

      files[`${imageName}.txt`] = yoloAnnotations;
    });

    return files;
  };

  const downloadFile = (content, filename, type = "application/json") => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    if (selectedFormats.length === 0) return;

    setIsExporting(true);
    setExportProgress(0);

    try {
      const projectName = selectedProject.projectName
        .replace(/[^a-z0-9]/gi, "_")
        .toLowerCase();
      const timestamp = new Date().toISOString().split("T")[0];

      for (let i = 0; i < selectedFormats.length; i++) {
        const format = selectedFormats[i];
        setExportProgress(((i + 1) / selectedFormats.length) * 100);

        await new Promise((resolve) => setTimeout(resolve, 500));

        switch (format) {
          case "json":
            downloadFile(
              generateJSONExport(),
              `${projectName}_annotations_${timestamp}.json`
            );
            break;
          case "csv":
            downloadFile(
              generateCSVExport(),
              `${projectName}_annotations_${timestamp}.csv`,
              "text/csv"
            );
            break;
          case "coco":
            downloadFile(
              generateCOCOExport(),
              `${projectName}_coco_${timestamp}.json`
            );
            break;
          case "yolo":
            const yoloFiles = generateYOLOExport();
            Object.entries(yoloFiles).forEach(([filename, content]) => {
              downloadFile(
                content,
                `${projectName}_yolo_${filename}`,
                "text/plain"
              );
            });
            break;
        }
      }

      setTimeout(() => {
        setIsExporting(false);
        setExportProgress(0);
        onClose();
      }, 1000);
    } catch (error) {
      console.error("Export failed:", error);
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  const getProjectStats = () => {
    const totalAnnotations = annotations.reduce(
      (total, img) => total + (img.annotations?.length || 0),
      0
    );
    const annotatedImages = annotations.filter(
      (img) => img.annotations?.length > 0
    ).length;
    const classes = [
      ...new Set(
        annotations.flatMap((img) =>
          img.annotations?.map((ann) => ann.class || "object") || []
        )
      ),
    ];

    return { totalAnnotations, annotatedImages, classes: classes.length };
  };

  if (!isOpen) return null;

  const stats = getProjectStats();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <Download className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">
                Export Annotations
              </h2>
              <p className="text-sm text-slate-600">
                {selectedProject.projectName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex max-h-[calc(90vh-80px)]">
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                <Package className="w-5 h-5 mr-2 text-blue-600" />
                Project Overview
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-2xl font-bold text-blue-700">
                        {images.length}
                      </div>
                      <div className="text-sm text-blue-600">Total Images</div>
                    </div>
                    <ImageIcon className="w-6 h-6 text-blue-500" />
                  </div>
                </div>
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 border border-emerald-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-2xl font-bold text-emerald-700">
                        {stats.annotatedImages}
                      </div>
                      <div className="text-sm text-emerald-600">Annotated</div>
                    </div>
                    <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                  </div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-2xl font-bold text-purple-700">
                        {stats.totalAnnotations}
                      </div>
                      <div className="text-sm text-purple-600">Annotations</div>
                    </div>
                    <Target className="w-6 h-6 text-purple-500" />
                  </div>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-2xl font-bold text-orange-700">
                        {stats.classes}
                      </div>
                      <div className="text-sm text-orange-600">Classes</div>
                    </div>
                    <Hash className="w-6 h-6 text-orange-500" />
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-blue-600" />
                Select Export Formats
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {exportFormats.map((format) => {
                  const Icon = format.icon;
                  const isSelected = selectedFormats.includes(format.id);
                  return (
                    <div
                      key={format.id}
                      onClick={() => handleFormatToggle(format.id)}
                      className={`relative cursor-pointer p-4 rounded-xl border-2 transition-all duration-200 ${
                        isSelected
                          ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200"
                          : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            isSelected
                              ? "bg-blue-500 text-white"
                              : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <h4
                              className={`font-semibold ${
                                isSelected ? "text-blue-800" : "text-slate-800"
                              }`}
                            >
                              {format.name}
                            </h4>
                            <div className="flex items-center space-x-2">
                              <span
                                className={`text-xs px-2 py-1 rounded-full ${
                                  format.size === "Small"
                                    ? "bg-emerald-100 text-emerald-700"
                                    : format.size === "Medium"
                                    ? "bg-yellow-100 text-yellow-700"
                                    : "bg-red-100 text-red-700"
                                }`}
                              >
                                {format.size}
                              </span>
                              {isSelected && (
                                <CheckCircle2 className="w-4 h-4 text-blue-600" />
                              )}
                            </div>
                          </div>
                          <p
                            className={`text-sm ${
                              isSelected ? "text-blue-700" : "text-slate-600"
                            }`}
                          >
                            {format.description}
                          </p>
                          <div
                            className={`text-xs mt-2 ${
                              isSelected ? "text-blue-600" : "text-slate-500"
                            }`}
                          >
                            File extension: {format.extension}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                <Settings className="w-5 h-5 mr-2 text-blue-600" />
                Export Options
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-center space-x-3 p-4 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={exportOptions.includeImages}
                      onChange={(e) =>
                        setExportOptions((prev) => ({
                          ...prev,
                          includeImages: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <div>
                      <div className="font-medium text-slate-800">
                        Include Images
                      </div>
                      <div className="text-sm text-slate-600">
                        Download images with annotations
                      </div>
                    </div>
                  </label>

                  <label className="flex items-center space-x-3 p-4 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={exportOptions.includeMetadata}
                      onChange={(e) =>
                        setExportOptions((prev) => ({
                          ...prev,
                          includeMetadata: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <div>
                      <div className="font-medium text-slate-800">
                        Include Metadata
                      </div>
                      <div className="text-sm text-slate-600">
                        Project info and export details
                      </div>
                    </div>
                  </label>

                  <label className="flex items-center space-x-3 p-4 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={exportOptions.normalizeCoordinates}
                      onChange={(e) =>
                        setExportOptions((prev) => ({
                          ...prev,
                          normalizeCoordinates: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <div>
                      <div className="font-medium text-slate-800">
                        Normalize Coordinates
                      </div>
                      <div className="text-sm text-slate-600">
                        Convert to 0-1 range (recommended for ML)
                      </div>
                    </div>
                  </label>

                  <label className="flex items-center space-x-3 p-4 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={exportOptions.splitDataset}
                      onChange={(e) =>
                        setExportOptions((prev) => ({
                          ...prev,
                          splitDataset: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <div>
                      <div className="font-medium text-slate-800">
                        Split Dataset
                      </div>
                      <div className="text-sm text-slate-600">
                        Create train/validation/test splits
                      </div>
                    </div>
                  </label>
                </div>

                {exportOptions.splitDataset && (
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <h4 className="font-medium text-slate-800 mb-3">
                      Dataset Split Configuration
                    </h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-2 block">
                          Training ({exportOptions.trainSplit}%)
                        </label>
                        <input
                          type="range"
                          min="50"
                          max="90"
                          value={exportOptions.trainSplit}
                          onChange={(e) => {
                            const train = parseInt(e.target.value);
                            const remaining = 100 - train;
                            const validation = Math.round(remaining * 0.6);
                            const test = remaining - validation;
                            setExportOptions((prev) => ({
                              ...prev,
                              trainSplit: train,
                              validationSplit: validation,
                              testSplit: test,
                            }));
                          }}
                          className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                        />
                      </div>
                      <div className="text-center">
                        <div className="text-sm font-medium text-slate-700 mb-2">
                          Validation ({exportOptions.validationSplit}%)
                        </div>
                        <div className="text-xs text-slate-500">
                          Auto-calculated
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm font-medium text-slate-700 mb-2">
                          Test ({exportOptions.testSplit}%)
                        </div>
                        <div className="text-xs text-slate-500">
                          Auto-calculated
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="w-80 border-l border-slate-200 bg-slate-50 p-6">
            <div className="sticky top-0">
              <div className="bg-white rounded-xl p-4 border border-slate-200 mb-6">
                <h3 className="font-semibold text-slate-800 mb-3 flex items-center">
                  <FolderDown className="w-4 h-4 mr-2 text-blue-600" />
                  Export Summary
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Selected Formats:</span>
                    <span className="font-medium text-slate-800">
                      {selectedFormats.length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Images to Export:</span>
                    <span className="font-medium text-slate-800">
                      {stats.annotatedImages}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Annotations:</span>
                    <span className="font-medium text-slate-800">
                      {stats.totalAnnotations}
                    </span>
                  </div>
                  <div className="border-t border-slate-200 pt-3 mt-3">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Estimated Size:</span>
                      <span className="font-medium text-slate-800">
                        {exportOptions.includeImages ? "50-100 MB" : "< 1 MB"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 border border-slate-200 mb-6">
                <h3 className="font-semibold text-slate-800 mb-3 flex items-center">
                  <Database className="w-4 h-4 mr-2 text-blue-600" />
                  Project Details
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <User className="w-3 h-3 text-slate-400" />
                    <span className="text-slate-600">Group:</span>
                    <span className="font-medium text-slate-800">
                      {selectedProject.annotationGroup}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-3 h-3 text-slate-400" />
                    <span className="text-slate-600">Created:</span>
                    <span className="font-medium text-slate-800">
                      {new Date(selectedProject.createdOn).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>

              {isExporting && (
                <div className="bg-white rounded-xl p-4 border border-slate-200 mb-6">
                  <h3 className="font-semibold text-slate-800 mb-3 flex items-center">
                    <Download className="w-4 h-4 mr-2 text-blue-600 animate-bounce" />
                    Exporting...
                  </h3>
                  <div className="space-y-3">
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-emerald-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${exportProgress}%` }}
                      ></div>
                    </div>
                    <div className="text-sm text-slate-600 text-center">
                      {exportProgress.toFixed(0)}% Complete
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <button
                  onClick={handleExport}
                  disabled={selectedFormats.length === 0 || isExporting}
                  className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  {isExporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Exporting...</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      <span>Export Annotations</span>
                    </>
                  )}
                </button>

                <button
                  onClick={onClose}
                  disabled={isExporting}
                  className="w-full py-3 px-4 bg-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  Cancel
                </button>

                {selectedFormats.length === 0 && (
                  <div className="flex items-center space-x-2 text-amber-600 text-sm p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>Please select at least one export format</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ViewAnnotationWithExport = () => {
  const location = useLocation()
  const [showExportModal, setShowExportModal] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const user = JSON.parse(localStorage.getItem("user"));
  const projectname =
    location.state?.projectname ||
    localStorage.getItem("projectname") ||
    "";
  const organizationId = localStorage.getItem("organizationId");

  useEffect(() => {
    const fetchAnnotationProjects = async () => {
      try {
        setProjectsLoading(true);
        const response = await axios.post("/fetch-annotation-image", {
          username: user.username,
          projectname: projectname,
          organizationId
        });

        if (response.status === 200) {
          const allAnnotations = response.data.annotationImage || [];

          const formattedProjects = allAnnotations.map((item) => ({
            projectName: item.projectname,
            annotationGroup: item.annotationGroup,
            images: item.images.map((img) => img.imageUrl),
            annotations: item.images.map((img) => ({
              imageUrl: img.imageUrl,
              annotations: img.annotations,
            })),
            createdOn: item.createdOn,
          }));

          setProjects(formattedProjects);
        }
      } catch (error) {
        console.error(error);
        message.error("Failed to fetch annotation projects");
      } finally {
        setProjectsLoading(false);
      }
    };

    fetchAnnotationProjects();
  }, [user.username]);

  const handleSelectProject = (project) => {
    setSelectedProject(project);
    setShowExportModal(true);
  };

  if (projectsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center">
        <div className="text-slate-600 text-lg">Loading projects...</div>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center">
        <div className="text-slate-600 text-lg">No projects available.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <div className="bg-white/80 backdrop-blur-sm border-b border-slate-200/60 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h1 className="text-xl font-bold text-slate-800 flex items-center">
                <Target className="w-5 h-5 mr-2 text-blue-600" />
                Annotation Projects
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-200/60">
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Target className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">
              Annotation Export
            </h2>
            <p className="text-slate-600 mb-6">
              This displays the annotations for the selected project.<br />
              Click the "Export" button on a project below to export its annotations.
            </p>
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center">
            <Package className="w-6 h-6 mr-2 text-blue-600" />
            All Projects
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <div
                key={project.projectName}
                className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-slate-200 hover:shadow-lg transition-all duration-200 cursor-pointer"
                onClick={() => handleSelectProject(project)}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-slate-800">
                    {project.projectName}
                  </h3>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectProject(project);
                    }}
                    className="flex items-center px-3 py-1.5 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-lg font-medium hover:from-emerald-700 hover:to-emerald-800 transition-all duration-200"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Export
                  </button>
                </div>
                <p className="text-sm text-slate-600 mb-4">
                  Group: {project.annotationGroup} • {project.images.length} images with annotations
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-3 border border-slate-200">
                    <div className="text-sm text-slate-600">Total Images</div>
                    <div className="text-lg font-bold text-blue-700">{project.images.length}</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-slate-200">
                    <div className="text-sm text-slate-600">Annotations</div>
                    <div className="text-lg font-bold text-emerald-700">
                      {project.annotations.reduce(
                        (total, img) => total + (img.annotations?.length || 0),
                        0
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {selectedProject && (
        <ExportAnnotationsModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          selectedProject={selectedProject}
          annotations={selectedProject.annotations}
          images={selectedProject.images}
        />
      )}
    </div>
  );
};

export default ViewAnnotationWithExport;